import React, {useCallback, useEffect, useRef, useState} from "react";
import Box from "../../lib/appfuse-react/component/Box";
import Paper from "../../lib/appfuse-react/component/Paper";
import {TabContext, TabList} from "@material-ui/lab";
import {Collapse, Divider, Grid, Popover, Tab, Typography} from "@material-ui/core";
import i18n from "../../lib/appfuse-react/core/i18n";
import Toolbar from "../../component/Toolbar";
import DataTable, {DataColumn} from "../../lib/appfuse-react/component/DataTable";
import Form from "../../lib/appfuse-react/component/Form";
import courseAnnouncementService from "../../service/courseAnnouncement/courseAnnouncementService";
import DataPagination from "../../lib/appfuse-react/component/DataPagination";
import lang from "../../lib/appfuse-react/core/lang";
import {makeStyles} from "@material-ui/core/styles";
import Button from "../../lib/appfuse-react/component/Button";
import Predicate from "../../lib/appfuse-react/core/Predicate";
import CircularProgress from "../../lib/appfuse-react/component/CircularProgress";
import {useHistory} from "react-router-dom";
import code from "../../lib/appfuse-react/core/code";
import SelectField from "../../lib/appfuse-react/component/SelectField";
import RichText from "../../lib/appfuse-react/component/RichText";
import time from "../../lib/appfuse-react/core/time";
import {useDispatch, useSelector} from "../../store";
import {setClassInfo} from "../../reducer/enrollment/enrollmentReducer";
import {isAuthenticated, setRedirect} from "../../reducer/iam/authReducer";
import usePrompt from "../../lib/appfuse-react/hook/usePrompt";
import {withDesktop} from "../../layout/Desktop";
import {withExplorer} from "../../layout/Explorer";
import enrollmentService from "../../service/enrollment/enrollmentService";
import DateField from "../../lib/appfuse-react/component/DateField";
import TextField from "../../lib/appfuse-react/component/TextField";
import Space from "../../lib/appfuse-react/component/Space";
import useResponsive from "../../lib/appfuse-react/hook/useResponsive";
import {KeyboardArrowDown, KeyboardArrowUp, Search} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
    link: {
        textDecoration: 'underline',
        textUnderlinePosition: 'under'
    },
    linkTypo: {
        color: '#005EDE',
        fontWeight: 400,
        fontSize: 18,
        lineHeight: '26px',
        textDecorationLine: 'underline',
        '&:hover': {
            cursor: 'pointer'
        }
    },
    popover: {
        '& .MuiPaper-rounded': {
            boxShadow: theme.shadows[5],
            borderRadius: '1rem',
            maxHeight: '85%',
            overflow: 'auto'
        },
        '& div.box': {
            boxShadow: theme.shadows[5],
            borderRadius: '1rem',
        }
    },
    tabPanel: {
        height: '100%',
        overflow: 'hidden',
        padding: '1rem'
    }
}));

function CourseAnnouncementContent() {
    const dispatch = useDispatch();
    const history = useHistory();
    const prompt = usePrompt();
    const classes = useStyles();
    const courseContentRef = useRef(null);
    const [ready, setReady] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [courseCategory, setCourseCategory] = useState([]);
    const [courseTitle, setCourseTitle] = useState([]);
    const [data, setData] = useState([]);
    const [selection, setSelection] = useState([]);
    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [sort, setSort] = useState(null);
    const [criteria, setCriteria] = useState({});
    const [courseContentOpen, setCourseContentOpen] = useState(false);
    const [courseContentData, setCourseContentData] = useState({});
    const [moreOptions, setMoreOptions] = useState(false);
    const authenticated = useSelector(isAuthenticated);

    const count = data.totalElements || 0;

    useEffect(() => {
        (async () => {
            await code.assert('classLocation', 'enrollmentStatus', 'enrollmentType');

            const courseCategory = await courseAnnouncementService.findCategories();
            setCourseCategory(courseCategory);

            const courseTitle = await courseAnnouncementService.findCourses();
            setCourseTitle(courseTitle);

            await search(tabValue,{}, {});

            dispatch(setRedirect(false));
            setReady(true);
        })();
    }, []);

    const search = async (tabValue, criteria, {sort, page, size}) => {
        const courseCategoryUuid = tabValue===0? undefined:tabValue;
        const predicate = Predicate.like('courseTitle.name', criteria?.name)
            .and(Predicate.eq('location', criteria.location))
            .and(Predicate.ge('courseDateStart', criteria.courseDateStart))
            .and(Predicate.le('courseDateStart', criteria.courseDateEnd))
            .and(Predicate.eq('status', criteria.status))
            .and(Predicate.eq('courseTitle.category.uuid', courseCategoryUuid));
        const data = await courseAnnouncementService.find({predicate, sort, page, size});
        setData(data);
    };
    const safeSearch = useCallback(lang.debounce(search, 1000), []);

    const handleSearchChange = (name) => ({value}) => {
        setCriteria({...criteria, [name]:value});
    };

    const handleSearch = (uuid) => {
        const courseCategoryUuid = lang.isNotEmpty(uuid)? uuid:tabValue;
        safeSearch(courseCategoryUuid, criteria, {sort, page:0, size:pageSize});
        setSelection([]);
        setPageNumber(0);
    };
    const handleSubmit = (event) => {
        if(event.key==="Enter") {
            handleSearch();
        }
    };

    const handleChangeTab = (event, newValue) => {
        setTabValue(newValue);
        if(tabValue!==newValue) {
            handleSearch(newValue);
        }
    };

    const tabData = [
        {uuid: 0, name: i18n.translate('Course Overview')},
        ...courseCategory
    ]

    const handleChangeTabSelect = (event) => {
        const {value} = event;
        setTabValue(value);
        if (tabValue !== value) {
            handleSearch(value);
        }
    }

    const handleSort = ({sort}) => {
        setSort(sort);
        setSelection([]);
        safeSearch(tabValue, criteria, {sort, page:pageNumber, size:pageSize});
    };
    const handleChangePageNumber = ({pageNumber}) => {
        setPageNumber(pageNumber);
        setSelection([]);
        safeSearch(tabValue, criteria, {sort, page:pageNumber, size:pageSize});
    };
    const handleChangePageSize = ({pageSize}) => {
        setPageSize(pageSize);
        setSelection([]);
        safeSearch(tabValue, criteria, {sort, page:pageNumber, size:pageSize});
    };

    const renderClassDate = (data) => {
        const {courseDateStart, courseDateEnd} = data;
        if(courseDateStart && courseDateEnd) {
            return <Typography>{`${time.format(courseDateStart, 'YYYY/MM/DD')} ~ ${time.format(courseDateEnd, 'YYYY/MM/DD')}`}</Typography>;
        } else {
            return <Typography>{''}</Typography>;
        }
    };

    /* 課程內容 */
    const handleCourseContentClose = () => setCourseContentOpen(false);
    const handleCourseContentShow = async (uuid) => {
        const classInfo = data.find(value => value.uuid===uuid);
        const {courseContent} = classInfo;
        const classContent = await courseAnnouncementService.getClassContent(courseContent);
        setCourseContentData(classContent);
        setSelection([uuid]);
        setCourseContentOpen(true);
    };

    /* 交通資訊 */
    const handleClassLocation = (uuid) => {
        history.push('/trafficInfo');
        setSelection([uuid]);
    };

    /* 報名/候補 */
    const handleEnrollment = async (uuid, value) => {
        if(authenticated) {
            try {
                await enrollmentService.validEnrollment({courseClassUuid:uuid});
                const classInfo = data.find(value => value.uuid===uuid);
                dispatch(setClassInfo(classInfo));
                history.push(`/enrollment/${uuid}/${value}`);
            } catch (e) {
                prompt.info(e);
            }
        } else {
            prompt.confirm('Please login first.', {actions:['Go to login', 'dismiss'],
                onAction: ({action}) => {
                    if(action==='Go to login') {
                        history.push('/logon');
                    }
                }});
        }
    };

    const handleSelect = (rowId) => {
        if(lang.first(selection)===rowId) setSelection([]);
        else setSelection([rowId]);
    };
    const handleCellClick = async (event) => {
        const {col, rowId, value} = event;
        if(col===0) {
            await handleCourseContentShow(rowId);
        } else if(col===3 && value!=='online') {
            handleClassLocation(rowId);
        } else if(col===5 && (value==='enrollment' || value==='waiting')) {
            await handleEnrollment(rowId, value);
        } else {
            handleSelect(rowId);
        }
    };

    const responsive = useResponsive();

    const bigScreen = responsive.isLargeScreen() || responsive.isMediumScreen();

    const handleMoreOptions = () => {
        setMoreOptions(!moreOptions);
    }

    const renderEnrollmentType = ({enrollmentType}) => {
        if (enrollmentType === 'noWaitlist') {
            return <Typography>{code.translate('enrollmentType', enrollmentType)}</Typography>
        } else {
            return <Typography className={classes.link}>{code.translate('enrollmentType', enrollmentType)}</Typography>;
        }
    }

    return (
        <Box fullHeight fullWidth>
            <CircularProgress value={ready} fullHeight fullWidth backgroundColor="white">
                <TabContext value={tabValue}>
                    <Box display='flex' fullHeight fullWidth>
                        {bigScreen && <Paper width='18%' fullHeight square backgroundOpacity={0.88}>
                            <TabList onChange={handleChangeTab} textColor='primary' orientation='vertical'
                                     style={{paddingTop: '1.5rem'}}>
                                <Tab key='all' label={i18n.translate('Course Overview')} value={0}/>
                                {courseCategory.map(type => <Tab key={type.uuid} label={type.name} value={type.uuid}/>)}
                            </TabList>
                        </Paper>}
                        <Paper fullWidth width='82%' square overflow='hidden'>
                            <Box fullHeight fullWidth display='flex' flexDirection='column' overflow='hidden' paddingX={2} ref={courseContentRef}>
                                {bigScreen && <Toolbar>
                                    <Box display='flex' alignItems='center' minWidth='33%' onKeyPress={handleSubmit}>
                                        {/*課程名稱*/}
                                        <Grid item xs={3}>
                                            <TextField value={criteria.name} label={i18n.translate("Course Name")} fullWidth size='small'
                                                       onChange={handleSearchChange('name')}/>
                                        </Grid>
                                        <Space spacing={2}/>
                                        {/*課程地點*/}
                                        <Grid item xs={3}>
                                            <SelectField value={criteria.location} label={i18n.translate("Class Location")} fullWidth size='small' code='classLocation'
                                                         onChange={handleSearchChange('location')}/>
                                        </Grid>
                                        <Space spacing={2}/>
                                        {/*課程日期(起)*/}
                                        <Grid item xs={3}>
                                            <DateField value={criteria.courseDateStart} label={i18n.translate("Course Date (Start)")} fullWidth size='small'
                                                       onChange={handleSearchChange('courseDateStart')}/>
                                        </Grid>
                                        <Space spacing={2}/>
                                        {/*課程日期(迄)*/}
                                        <Grid item xs={3}>
                                            <DateField value={criteria.courseDateEnd} label={i18n.translate("Course Date (End)")} fullWidth size='small'
                                                       onChange={handleSearchChange('courseDateEnd')}/>
                                        </Grid>
                                        <Space spacing={2}/>
                                        {/*狀態*/}
                                        <Grid item xs={3}>
                                            <SelectField value={criteria.status} label={i18n.translate("Enrollment Status")} fullWidth size='small' code='enrollmentStatus'
                                                         onChange={handleSearchChange('status')}/>
                                        </Grid>
                                        <Box paddingLeft={1}>
                                            <Button icon={<Search/>} color='primary' variant='icon' onClick={() => {handleSearch()}}/>
                                        </Box>
                                    </Box>
                                </Toolbar>}
                                {bigScreen ?
                                    <Box paddingX={2} overflow="hidden">
                                        <DataTable data={data} sort={sort} onSort={handleSort} selection={selection}
                                                   pageNumber={pageNumber} pageSize={pageSize} onCellClick={handleCellClick}
                                                   rowId='uuid' stickyHeader maxHeight='100%'>
                                            <DataColumn accessor='code' label='Course No' sortable renderer={(data) => <Typography className={classes.link}>{data.code}</Typography>} minWidth='10rem'/>
                                            <DataColumn accessor='courseTitle' label='Course Name'/>
                                            <DataColumn accessor={renderClassDate} label='Class Date'/>
                                            <DataColumn accessor='location' label='Class Location' sortable renderer={({location}) => <Typography className={location!=='online' && classes.link}>{code.translate('classLocation', location)}</Typography>} minWidth='8rem' align='left'/>
                                            <DataColumn accessor='enrollmentStatus' label='Enrollment Status' formatter={data => code.translate('enrollmentStatus', data)} minWidth='7rem'/>
                                            <DataColumn accessor='enrollmentType' label='Enrollment Type' renderer={renderEnrollmentType}/>
                                            <DataColumn accessor='remark' label='Remark'/>
                                        </DataTable>
                                    </Box> :
                                    <Box fullWidth overflow="auto" paddingTop={2}>
                                        <Box display={'flex'} flexWrap={'wrap'} fullWidth gap={1}>
                                            <SelectField value={tabValue} label={i18n.translate('Course Category')} variant={'outlined'} fullWidth options={tabData} size={'small'} textAccessor={'name'} valueAccessor={'uuid'} onChange={handleChangeTabSelect}/>
                                            <Collapse in={moreOptions}>
                                                <Box display={'flex'} flexWrap={'wrap'} fullWidth gap={1}>
                                                    <TextField value={criteria.name} label={i18n.translate("Course Name")} variant={'outlined'} fullWidth size='small'
                                                               onChange={handleSearchChange('name')}/>
                                                    <SelectField value={criteria.location} label={i18n.translate("Class Location")} variant={'outlined'} fullWidth size='small' code='classLocation'
                                                                 onChange={handleSearchChange('location')}/>
                                                    <DateField value={criteria.courseDateStart} label={i18n.translate("Course Date (Start)")} variant={'outlined'} fullWidth size='small'
                                                               onChange={handleSearchChange('courseDateStart')}/>
                                                    <DateField value={criteria.courseDateEnd} label={i18n.translate("Course Date (End)")} variant={'outlined'} fullWidth size='small'
                                                               onChange={handleSearchChange('courseDateEnd')}/>
                                                    <SelectField value={criteria.status} label={i18n.translate("Enrollment Status")} fullWidth variant={'outlined'} size='small' code='enrollmentStatus'
                                                                 onChange={handleSearchChange('status')}/>
                                                </Box>
                                            </Collapse>
                                            <Box fullWidth>
                                                <Button icon={!moreOptions?<KeyboardArrowDown/>:<KeyboardArrowUp/>} text={!moreOptions?'更多選項':'更少選項'} color='primary' variant='text' fullWidth onClick={handleMoreOptions}/>
                                            </Box>
                                            <Box fullWidth>
                                                <Button icon={<Search/>} text={'Search'} color='primary' variant='contained' fullWidth onClick={() => {handleSearch()}}/>
                                            </Box>
                                        </Box>
                                        <CourseMobileBox data={data} handleCourseContentShow={handleCourseContentShow} handleEnrollment={handleEnrollment} handleClassLocation={handleClassLocation}/>
                                    </Box>
                                }
                                <Box paddingX={bigScreen?2:0}>
                                    <DataPagination count={count} pageNumber={pageNumber} pageSize={pageSize}
                                                    onChangePageSize={handleChangePageSize} onChangePageNumber={handleChangePageNumber}/>
                                </Box>
                                {courseContentOpen && <CourseContent open={courseContentOpen} data={courseContentData} anchorEl={courseContentRef}
                                                                     onClose={handleCourseContentClose} courseTitle={courseTitle}/>}
                            </Box>
                        </Paper>
                    </Box>
                </TabContext>
            </CircularProgress>
        </Box>
    );
}

function CourseMobileBox(props) {
    const classes = useStyles()
    const {data = []} = props;
    const {handleCourseContentShow = (uuid) => {}, handleClassLocation = (uuid) => {}, handleEnrollment = (uuid, value) => {}} = props;

    const CourseBox = data.map((value, index) => {
        const {courseDateStart, courseDateEnd, code:CourseCode, courseTitle, location, enrollmentStatus, enrollmentType, remark, uuid} = value;

        return (
            <React.Fragment>
                <Box key={index} fullWidth display='flex' padding={2} flexDirection={'column'} justifyContent={'center'} gap={1}>
                    <Typography variant='body1' className={classes.link} onClick={async () => {
                        await handleCourseContentShow(uuid)
                    }}>{CourseCode}</Typography>
                    <Typography variant='h6'>{courseTitle}</Typography>
                    <Typography variant='body1'>{`${time.format(courseDateStart, 'YYYY/MM/DD')} ~ ${time.format(courseDateEnd, 'YYYY/MM/DD')}`}</Typography>
                    <Typography variant='body1' className={classes.link} onClick={() => {handleClassLocation(uuid)}}>{`課程地點：${code.translate('classLocation', location)}`}</Typography>
                    <Typography variant='body1'>{`狀態：${code.translate('enrollmentStatus', enrollmentStatus)}`}</Typography>
                    <Typography variant='body1' className={classes.link} onClick={() => {handleEnrollment(uuid, enrollmentType)}}>{code.translate('enrollmentType', enrollmentType)}</Typography>
                    <Typography variant='body1'>{remark}</Typography>
                </Box>
                <Divider/>
            </React.Fragment>
        )
    })


    return (
        <Box fullWidth>
            {CourseBox}
        </Box>
    )
}

function CourseContent(props) {
    const {open, anchorEl, data, courseTitle=[]} = props;
    const {courseTimeDescriptions} = data;
    const classes = useStyles();

    const getClassDate = () => {
        const {courseDateStart, courseDateEnd} = data;
        if(courseDateStart && courseDateEnd) {
            return `${time.format(courseDateStart, 'YYYY/MM/DD')} ~ ${time.format(courseDateEnd, 'YYYY/MM/DD')}`;
        } else {
            return '';
        }
    };
    const classDate = getClassDate();

    const responsive = useResponsive();
    const isMobile = !responsive.isLargeScreen();

    const xsOne = isMobile? 12:4;
    const xsTwo = isMobile? 6:4;

    return (
        <Popover open={open} anchorEl={anchorEl.current} onClose={props.onClose} className={classes.popover}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
        >
            <Box padding={4} maxWidth='60rem' className='box'>
                <Form value={data} readOnly>
                    <Grid container spacing={2}>
                        { isMobile &&
                            <Grid item xs={12}>
                                <Box fullWidth display={'flex'} flexGrow={'flex-end'}>
                                    <Box marginLeft={'auto'}>
                                        <Button text='Close' variant='text' color='primary' onClick={props.onClose}/>
                                    </Box>
                                </Box>
                            </Grid>
                        }
                        {/*課程代碼*/}
                        <Grid item xs={xsOne}>
                            <TextField name='code' label='Course No' fullWidth/>
                        </Grid>
                        {/*課程名稱*/}
                        <Grid item xs={xsOne}>
                            <TextField name='courseTitle' label='Course Name' fullWidth multiline/>
                        </Grid>
                        {/*課程日期*/}
                        <Grid item xs={xsOne}>
                            <TextField name='courseDate' label='Class Date' fullWidth value={classDate}/>
                        </Grid>
                        {/*課程地點*/}
                        <Grid item xs={xsOne}>
                            <SelectField name='location' label='Class Location' fullWidth code='classLocation'/>
                        </Grid>
                        {/*招收人數*/}
                        <Grid item xs={xsTwo}>
                            <TextField name='enrollment' label='Enrollment Number' fullWidth/>
                        </Grid>
                        {/*費用*/}
                        <Grid item xs={xsTwo}>
                            <TextField name='cost' label='Cost' fullWidth/>
                        </Grid>
                        {/*學員資格*/}
                        <Grid item xs={12}>
                            <TextField name='studentQualification' label='Student Qualification' fullWidth multiline/>
                        </Grid>
                        {/*先修課程*/}
                        <Grid item xs={12}>
                            <SelectField name='firstCourse' label='First Course' fullWidth multiline
                                         options={courseTitle} valueAccessor='uuid' textAccessor='name' />
                        </Grid>
                        {/*課程說明*/}
                        <Grid item xs={12}>
                            <Typography variant='caption' color='textSecondary'>{i18n.translate('Course Description')}</Typography>
                            <RichText name='description' label='Course Description' fullWidth padding={1} border='1px solid rgba(0, 0, 0, 0.42)' borderRadius={1}/>
                        </Grid>
                        {/*課程大綱*/}
                        <Grid item xs={12}>
                            <Typography variant='caption' color='textSecondary'>{i18n.translate('Course Outline')}</Typography>
                            <RichText name='outline' label='Course Outline' fullWidth padding={1} border='1px solid rgba(0, 0, 0, 0.42)' borderRadius={1}/>
                        </Grid>
                        {/*課程時段的時間說明*/}
                        {lang.isNotEmpty(courseTimeDescriptions) && lang.isNotNullOrUndefined(courseTimeDescriptions) &&
                            <Grid item xs={12}>
                                <Typography variant='caption' color='textSecondary'>{i18n.translate('Course Time Period Description')}</Typography>
                                <DataTable data={courseTimeDescriptions} dense>
                                    <DataColumn accessor='timePeriod' label='時段'/>
                                    <DataColumn accessor='startEndTime' label='起迄時間'/>
                                </DataTable>
                            </Grid>
                        }
                        {/*備註*/}
                        <Grid item xs={12}>
                            <Typography variant='caption' color='textSecondary'>{i18n.translate('Remark')}</Typography>
                            <RichText name='remark' label='Remark' fullWidth padding={1} border='1px solid rgba(0, 0, 0, 0.42)' borderRadius={1}/>
                        </Grid>
                    </Grid>
                </Form>
            </Box>
        </Popover>
    );
}

function CourseAnnouncement() {
    const authenticated = useSelector(isAuthenticated);

    return authenticated? withDesktop(CourseAnnouncementContent)():withExplorer(CourseAnnouncementContent)();
}

export default CourseAnnouncement;
